import React, { useState } from 'react';
import type { VisitsSourceCard as SourceCard } from '@troia-v3/types';

import { Button, CardActionsMenu, StatusPill, Tooltip } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { DashCard } from '@/shared/components/ui/dashboard';

import { viewSourcesApi } from '../services/viewSourcesApi';

interface Props {
  sources: SourceCard[];
  onChanged: () => void;
  onConnect: () => void;
  onEdit: (source: SourceCard) => void;
}

const relativeSince = (iso: string | null): string => {
  if (!iso) return 'ainda não sincronizado';
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'agora há pouco';
  if (diffMin < 60) return `há ${diffMin} min`;
  const hours = Math.round(diffMin / 60);
  if (hours < 24) return `há ${hours}h`;
  return `há ${Math.round(hours / 24)} dias`;
};

/**
 * Fontes conectadas, com o estado que decide se o número da tela é confiável.
 *
 * Mostrar `lastError` aqui é o que fecha o loop: hoje o sync de bases de dados
 * grava o erro em lugar nenhum e a falha morre no log — o operador vê o número
 * parar de subir sem nunca saber por quê.
 */
export const VisitsSourcesCard: React.FC<Props> = ({ sources, onChanged, onConnect, onEdit }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const confirm = useConfirm();

  /**
   * Desconectar apaga as visitas junto — não dá para fazer isso num clique.
   *
   * A mensagem diz o NÚMERO de visitas que some, não um "tem certeza?" genérico:
   * é a diferença entre confirmar e entender o que se está confirmando.
   */
  const handleDisconnect = async (source: SourceCard): Promise<void> => {
    const ok = await confirm({
      title: 'Desconectar fonte de visitas',
      message: `Desconectar "${source.name}" remove a integração e apaga todas as visitas que vieram dela.`,
      warningMessage: 'As visitas não voltam. Só reconectando e sincronizando de novo.',
      confirmButtonText: 'Desconectar e apagar',
      variant: 'danger',
    });
    if (!ok) return;
    await run(source.integrationId, () => viewSourcesApi.disconnect(source.integrationId));
  };

  const run = async (id: string, action: () => Promise<void>): Promise<void> => {
    setBusy(id);
    try {
      await action();
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashCard
      icon="activity"
      title="Fontes de visitas"
      subtitle={sources.length === 0 ? 'Nenhuma fonte conectada' : `${sources.length} conectada(s)`}
      headerRight={
        <Button size="sm" onClick={onConnect} data-testid="analytics-connect-source">
          Conectar fonte
        </Button>
      }
      testId="analytics-visits-sources-card"
    >
      {sources.length === 0 ? (
        <div className="text-sm py-4" style={{ color: 'var(--theme-muted-text)' }}>
          Conecte um contador de fluxo ou o analytics do seu site para medir visitantes de verdade.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sources.map((source) => (
            <div
              key={source.integrationId}
              className="flex items-start justify-between gap-3 flex-wrap"
              data-testid={`analytics-source-${source.integrationId}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                    {source.name}
                  </span>
                  <StatusPill
                    variant={source.needsReconnect || source.lastError ? 'error' : 'success'}
                    label={source.needsReconnect ? 'Reconectar' : source.lastError ? 'Com erro' : 'Ativa'}
                    data-testid={`analytics-source-status-${source.integrationId}`}
                  />
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                  {source.providerName} · sincronizado {relativeSince(source.lastSyncedAt)}
                </div>
                {source.lastError && (
                  <div
                    className="text-xs mt-1"
                    style={{ color: 'var(--theme-error-fg)' }}
                    data-testid={`analytics-source-error-${source.integrationId}`}
                  >
                    {source.needsReconnect
                      ? 'Credencial recusada. Reconecte a fonte para voltar a receber dados.'
                      : source.lastError}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Sincronizar é a ação frequente e cabe num ícone; editar e
                    desconectar moram no ••• como em toda listagem do sistema. */}
                <Tooltip content="Sincronizar agora">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="refresh"
                    aria-label="Sincronizar agora"
                    disabled={busy === source.integrationId}
                    onClick={() =>
                      void run(source.integrationId, () => viewSourcesApi.syncNow(source.integrationId))
                    }
                    data-testid={`analytics-source-sync-${source.integrationId}`}
                  />
                </Tooltip>
                <CardActionsMenu
                  size="sm"
                  data-testid={`analytics-source-actions-${source.integrationId}`}
                  actions={[
                    {
                      label: 'Editar',
                      icon: 'edit',
                      onClick: () => { onEdit(source); },
                      disabled: busy === source.integrationId,
                    },
                    {
                      label: 'Desconectar',
                      icon: 'trash',
                      variant: 'danger',
                      disabled: busy === source.integrationId,
                      onClick: () => void handleDisconnect(source),
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </DashCard>
  );
};

VisitsSourcesCard.displayName = 'VisitsSourcesCard';
